import type { Node } from 'unist';
import { visit } from 'unist-util-visit';

import { emojisByShortname } from '@gitbook/util-emojis';

const RE_EMOJI = /:\+1:|:-1:|:[\w-]+:/g;

/**
 * Unified plugin to parse emoji from markdown.
 */
export const remarkEmojis = function remarkEmojis() {
    return (tree) => {
        visit(tree, 'text', (node, index, parent) => {
            const matches = emojiHandler(node);

            if (!matches) {
                return;
            }

            parent.children.splice(index, 1, ...matches);
        });
    };
};

/**
 * Extract any emojis of the form :+1: :joy: etc. into GitBook custom inlines.
 *
 * Returns an array of nodes if any emojis were extracted, or null if no emojis were
 * extracted.
 */
function emojiHandler(node: Node): Node[] | null {
    // @ts-expect-error
    const value = node.value;
    if (typeof value !== 'string') {
        return null;
    }

    const emojiMatches = value.matchAll(RE_EMOJI);
    const result: Node[] = [];
    let matched = false;
    let lastIndex = 0;

    //
    // Iterate over all emoji matches in the text. When we encounter a match,
    // we push all text seen since the last match as regular text, and then
    // push the emoji as a custom raw element to be picked up by hast2gast.
    //

    for (const match of emojiMatches) {
        const shortname = match[0];
        const startIndex = match.index;
        const endIndex = match.index + shortname.length;

        const emoji = emojisByShortname[shortname];

        // GitBook doesn't support this emoji, just leave it as its shortname.
        if (!emoji) {
            continue;
        }

        matched = true;

        // Push any regular text seen since the last match.
        result.push({
            type: 'text',
            // @ts-expect-error
            value: value.slice(lastIndex, startIndex),
        });

        // Push the emoji as a raw element
        result.push({
            type: 'html',
            // @ts-expect-error
            value: `<span data-gb-custom-inline data-tag="emoji" data-code="${emoji.code}">${shortname}</span>`,
        });

        lastIndex = endIndex;
    }

    if (matched) {
        // Push any remaining text between the last seen emoji and the end
        // of the string
        result.push({
            type: 'text',
            // @ts-expect-error
            value: value.slice(lastIndex),
        });

        return result;
    }

    return null;
}
